import type { PursuitConfig } from '../config/pursuit.config';
import { clamp } from '../utils/math';

/**
 * The pursuer group as a single "gap" value (meters behind the player).
 * A visual pursuit system (characters running behind the player) reads this
 * value later – no full player physics for pursuers.
 */
export class PursuitModel {
  private gapMeters = 0;

  constructor(private readonly config: PursuitConfig) {
    this.reset();
  }

  get gap(): number {
    return this.gapMeters;
  }

  get isCritical(): boolean {
    return this.gapMeters <= this.config.criticalGap;
  }

  /** 0 = far away, 1 = caught – for UI/audio intensity. */
  get pressure(): number {
    return 1 - clamp(this.gapMeters / this.config.maxGap, 0, 1);
  }

  reset(): void {
    this.gapMeters = this.config.startGap;
  }

  update(dt: number): void {
    this.gapMeters = Math.min(this.config.maxGap, this.gapMeters + this.config.recoveryPerSecond * dt);
  }

  /** Applies a stumble penalty. Returns true if the pursuers caught up. */
  applyStumble(): boolean {
    this.gapMeters -= this.config.stumblePenalty;
    return this.gapMeters <= 0;
  }

  /** Player escaped by spending an extra life. */
  rescue(): void {
    this.gapMeters = this.config.gapAfterRescue;
  }
}
